/**
 * Collections Explorer Menu
 * Renders the mobile menu drawer's "Collection -> Collections" navigation
 * (Categories, Brands, Boutiques tabs) from the CMS collections-explorer
 * endpoint:
 *   GET {data-api-url} (defaults to https://cms.mawsim.co/api/collections-explorer)
 *
 * Collection URLs are read directly from the CMS payload
 * (collection_url_en / collection_url_ar / collection_url). This shop has no
 * locale URL subfolders - Arabic is the default/root domain - so any leading
 * "/ar" segment the CMS includes in collection_url_ar is stripped before use.
 *
 * The Storefront API handle lookup (batched `nodes` query, using the public
 * Storefront Access Token already configured in theme settings -
 * settings.recombee_storefront_token, see snippets/recombee-scripts.liquid)
 * is only used as a fallback, for the rare item that has a collection_id but
 * no usable collection_url* field from the CMS.
 *
 * Markup is built entirely client-side since the source data lives outside
 * Shopify. See snippets/collections-explorer-menu.liquid for the host markup
 * this element expects (tabs/panels containers + icon <template>s).
 */
(function () {
  var CACHE_KEY = 'collections_explorer_cache_v1';
  var CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

  var HANDLES_CACHE_KEY = 'collections_explorer_handles_cache_v1';
  var HANDLES_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour - handles rarely change
  var STOREFRONT_API_VERSION = '2024-01';

  var dataPromise = null;
  var handleMapPromise = null;

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || !cached.timestamp || !cached.data) return null;
      if (Date.now() - cached.timestamp > CACHE_TTL_MS) return null;
      return cached.data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data: data }));
    } catch (e) {
      // Ignore quota/private-mode errors - caching is a nice-to-have.
    }
  }

  function fetchExplorerData(apiUrl) {
    if (dataPromise) return dataPromise;

    var cached = readCache();
    if (cached) {
      dataPromise = Promise.resolve(cached);
      return dataPromise;
    }

    dataPromise = fetch(apiUrl)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP error! Status: ' + res.status);
        return res.json();
      })
      .then(function (result) {
        var data = result && result.success && result.data ? result.data : null;
        if (!data) throw new Error('Unexpected response payload');
        writeCache(data);
        return data;
      })
      .catch(function (e) {
        console.warn('[Collections Explorer Menu] Failed to load menu data:', e);
        dataPromise = null; // allow a retry on the next attempt
        throw e;
      });

    return dataPromise;
  }

  // Some CMS entries don't have a real handle yet - instead of leaving the
  // field blank, the backend fills collection_url(_en|_ar) with the raw
  // collection_id itself (e.g. "/collections/475669823708"). That's not a
  // working Shopify URL (Shopify 301-redirects unknown /collections/{id}
  // paths to the homepage - confirmed by testing), so it must not be trusted
  // as-is. Treat it the same as a missing URL so the Storefront API gets a
  // chance to resolve the real handle from the id instead.
  function isPlaceholderCmsUrl(url, id) {
    if (!url || !id) return false;
    var match = String(url).replace(/^\/ar(?=\/|$)/, '').match(/\/collections\/([^/?#]+)\/?$/);
    var slug = match ? decodeURIComponent(match[1]) : '';
    return slug === String(id).trim();
  }

  // An item is considered to have a usable CMS URL if any of its
  // collection_url* fields are populated with something other than the
  // "id-as-handle" placeholder described above - in that case we never need
  // the Storefront API for it.
  function hasCmsUrl(item) {
    if (!item) return false;
    var id = item.collection_id != null ? String(item.collection_id).trim() : '';
    return [item.collection_url_en, item.collection_url_ar, item.collection_url].some(function (url) {
      return !!url && !isPlaceholderCmsUrl(url, id);
    });
  }

  // Walks the CMS payload and collects the collection_id of every item that
  // has no usable collection_url* field (missing, or just the id-as-handle
  // placeholder), whether inside a group's group_collections or in the
  // top-level brands array. These are the only items that need a Storefront
  // API handle lookup as a fallback.
  function collectCollectionIds(data) {
    var ids = {};

    (Array.isArray(data && data.collections_explorer) ? data.collections_explorer : []).forEach(function (group) {
      (group && Array.isArray(group.group_collections) ? group.group_collections : []).forEach(function (child) {
        if (child && child.collection_id != null && !hasCmsUrl(child)) ids[String(child.collection_id).trim()] = true;
      });
    });

    (Array.isArray(data && data.brands) ? data.brands : []).forEach(function (brand) {
      if (brand && brand.collection_id != null && !hasCmsUrl(brand)) ids[String(brand.collection_id).trim()] = true;
    });

    return Object.keys(ids).filter(Boolean);
  }

  function readHandlesCache() {
    try {
      var raw = sessionStorage.getItem(HANDLES_CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || !cached.timestamp || !cached.map) return null;
      if (Date.now() - cached.timestamp > HANDLES_CACHE_TTL_MS) return null;
      return cached.map;
    } catch (e) {
      return null;
    }
  }

  function writeHandlesCache(map) {
    try {
      sessionStorage.setItem(HANDLES_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), map: map }));
    } catch (e) {
      // Ignore quota/private-mode errors - caching is a nice-to-have.
    }
  }

  // Resolves a batch of numeric collection IDs to their real Shopify handles
  // via a single Storefront API `nodes` query, so links use the canonical
  // /collections/{handle} URL rather than a guessed path. Results are cached
  // for an hour since collection handles rarely change.
  function resolveCollectionHandles(ids, token) {
    if (handleMapPromise) return handleMapPromise;

    if (!token || !ids.length) {
      handleMapPromise = Promise.resolve({});
      return handleMapPromise;
    }

    var cached = readHandlesCache();
    if (cached && ids.every(function (id) { return Object.prototype.hasOwnProperty.call(cached, id); })) {
      handleMapPromise = Promise.resolve(cached);
      return handleMapPromise;
    }

    var gids = ids.map(function (id) { return 'gid://shopify/Collection/' + id; });
    var query = 'query getCollectionHandles { nodes(ids: ' + JSON.stringify(gids) + ') { ... on Collection { id handle } } }';

    handleMapPromise = fetch('/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: query })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Storefront API error: ' + res.status);
        return res.json();
      })
      .then(function (result) {
        var map = {};
        var nodes = result && result.data && result.data.nodes ? result.data.nodes : [];
        nodes.forEach(function (node) {
          if (!node || !node.handle) return;
          var numericId = node.id.replace('gid://shopify/Collection/', '');
          map[numericId] = node.handle;
        });
        writeHandlesCache(map);
        return map;
      })
      .catch(function (e) {
        console.warn('[Collections Explorer Menu] Failed to resolve collection handles via Storefront API:', e);
        handleMapPromise = null; // allow a retry on the next attempt
        return {};
      });

    return handleMapPromise;
  }

  function localeText(en, ar, locale) {
    if (locale === 'ar') return ar || en || '';
    return en || ar || '';
  }

  // This shop does not use locale URL subfolders (e.g. /ar/, /en/) - Arabic
  // is the default/root domain and language switching is handled via a
  // cookie-based form post (see snippets/localization-selector.liquid), not a
  // URL prefix. The CMS still sends collection_url_ar with a "/ar" segment,
  // so strip it before using any CMS-provided URL.
  function stripArPrefix(url) {
    return url.replace(/^\/ar(?=\/|$)/, '') || '/';
  }

  function safeHref(url) {
    return typeof url === 'string' && url.charAt(0) === '/' ? stripArPrefix(url) : '#';
  }

  // Prefers the CMS-provided collection_url(_en|_ar) fields (with any leading
  // "/ar" segment stripped) - but only when that field isn't the "id-as-
  // handle" placeholder described above isPlaceholderCmsUrl. When it is (or
  // when there's no CMS URL at all), prefer the Storefront-API-resolved
  // handle (canonical /collections/{handle} URL). As an absolute last
  // resort, fall back to the placeholder CMS URL or a path built directly
  // from collection_id - note this does NOT reliably work: Shopify
  // 301-redirects unknown /collections/{numeric_id} paths to the homepage
  // (confirmed by testing), so this only helps when the id happens to also
  // be a valid handle.
  function localizedHref(item, locale, handleMap) {
    var cmsUrl = (locale === 'ar' ? item.collection_url_ar : item.collection_url_en) || item.collection_url;
    var id = item && item.collection_id != null ? String(item.collection_id).trim() : '';
    var isPlaceholder = isPlaceholderCmsUrl(cmsUrl, id);

    if (cmsUrl && !isPlaceholder) return safeHref(cmsUrl);

    var handle = id && handleMap ? handleMap[id] : null;
    if (handle) return '/collections/' + handle;

    if (cmsUrl) return safeHref(cmsUrl);
    if (id) return '/collections/' + encodeURIComponent(id);

    return '#';
  }

  // The CMS's image host (assets.mawsim.co, an S3/CloudFront bucket) serves
  // raw, un-resized originals with no resizing query params supported - a
  // single group/collection thumbnail can be 200-300KB+ even though it's
  // only ever displayed at a few dozen pixels wide here. That's the main
  // reason these images can feel slow to appear the first time the drawer
  // is opened (many large images all starting to download at once).
  //
  // images.weserv.nl is a public read-through image resizing proxy: it
  // fetches the original once, resizes/re-encodes it, and serves the result
  // with a long-lived Cache-Control (verified: a 290KB source image comes
  // back at ~2KB through the proxy). No CMS/backend changes are required.
  var IMAGE_PROXY_HOST = 'images.weserv.nl';

  function resizeImageUrl(url, width, height, quality) {
    if (typeof url !== 'string' || url.indexOf('http') !== 0) return url;

    var bare = url.replace(/^https?:\/\//, '');
    var params = 'w=' + width + '&h=' + height + '&fit=cover&a=attention&output=webp&q=' + (quality || 75);
    return 'https://' + IMAGE_PROXY_HOST + '/?url=' + encodeURIComponent(bare) + '&' + params;
  }

  function el(tag, attrs, text) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        if (attrs[key] === undefined || attrs[key] === null) return;
        if (key === 'class') node.className = attrs[key];
        else node.setAttribute(key, attrs[key]);
      });
    }
    if (text) node.textContent = text;
    return node;
  }

  function buildGridItem(item, locale, isCircle, handleMap) {
    var name = localeText(item.collection_name_en, item.collection_name_ar, locale);
    var image = isCircle
      ? (locale === 'ar' ? item.collection_image_ar : item.collection_image_en) || item.collection_image_en || item.collection_image_ar
      : item.collection_image;

    var a = el('a', {
      class: 'collections-explorer__grid-item' + (isCircle ? ' collections-explorer__grid-item--circle' : ''),
      href: localizedHref(item, locale, handleMap)
    });

    var media = el('span', { class: 'collections-explorer__grid-media' });
    if (image) {
      // Grid tiles are ~1/3 of the drawer's ~400px max width (~115px wide,
      // ~153px tall at the 3:4 aspect ratio for non-circle tiles). Request
      // ~3x that so the image isn't upscaled (and looking pixelated) on
      // higher-density (dpr 3) phone screens.
      var gridSrc = isCircle ? resizeImageUrl(image, 200, 200) : resizeImageUrl(image, 360, 480);
      media.appendChild(el('img', { src: gridSrc, alt: name, loading: 'lazy', decoding: 'async' }));
    }

    a.appendChild(media);
    a.appendChild(el('span', { class: 'collections-explorer__grid-label' }, name));
    return a;
  }

  function buildGridPanel(items, locale, isCircle, handleMap) {
    var panel = el('div', { class: 'collections-explorer__panel', role: 'tabpanel' });
    var grid = el('div', { class: 'collections-explorer__grid' + (isCircle ? ' collections-explorer__grid--circle' : '') });

    items.forEach(function (item) {
      grid.appendChild(buildGridItem(item, locale, isCircle, handleMap));
    });

    panel.appendChild(grid);
    return panel;
  }

  function buildCategoriesPanel(groups, locale, icons, handleMap) {
    var panel = el('div', { class: 'collections-explorer__panel', role: 'tabpanel' });
    var listView = el('div', { class: 'collections-explorer__list-view' });
    var detailView = el('div', { class: 'collections-explorer__detail-view' });
    detailView.hidden = true;

    var list = el('ul', { class: 'collections-explorer__list' });

    var backButton = el('button', { type: 'button', class: 'collections-explorer__back-button' });
    if (icons.back) backButton.appendChild(icons.back.cloneNode(true));
    var backLabel = el('span', {}, '');
    backButton.appendChild(backLabel);

    var grid = el('div', { class: 'collections-explorer__grid' });

    function showDetail(group) {
      var children = (group.group_collections || []).filter(function (c) { return c && c.is_active; });

      backLabel.textContent = localeText(group.group_name_en, group.group_name_ar, locale);
      grid.innerHTML = '';
      children.forEach(function (child) {
        grid.appendChild(buildGridItem(child, locale, false, handleMap));
      });

      listView.hidden = true;
      detailView.hidden = false;
    }

    groups.forEach(function (group) {
      var name = localeText(group.group_name_en, group.group_name_ar, locale);
      var li = el('li', { class: 'collections-explorer__list-item' });
      var button = el('button', { type: 'button', class: 'collections-explorer__list-button' });
      var thumb = el('span', { class: 'collections-explorer__list-thumb' });

      if (group.group_image) {
        // List thumb is 44x44 in CSS; request ~4x that (176x176) at a higher
        // quality (85) so it looks crisp even on higher-density (dpr 3)
        // phone screens - still well under 6KB through the resize proxy.
        thumb.appendChild(el('img', { src: resizeImageUrl(group.group_image, 176, 176, 85), alt: name, loading: 'lazy', decoding: 'async' }));
      }

      button.appendChild(thumb);
      button.appendChild(el('span', { class: 'collections-explorer__list-label' }, name));
      if (icons.forward) button.appendChild(icons.forward.cloneNode(true));
      button.addEventListener('click', function () { showDetail(group); });

      li.appendChild(button);
      list.appendChild(li);
    });

    backButton.addEventListener('click', function () {
      detailView.hidden = true;
      listView.hidden = false;
    });

    listView.appendChild(list);
    detailView.appendChild(backButton);
    detailView.appendChild(grid);
    panel.appendChild(listView);
    panel.appendChild(detailView);
    return panel;
  }

  var CollectionsExplorerMenu = class extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.locale = this.dataset.locale === 'ar' ? 'ar' : 'en';
      this.apiUrl = this.dataset.apiUrl || 'https://cms.mawsim.co/api/collections-explorer';
      this.storefrontToken = this.dataset.storefrontToken || '';
      this.tabsEl = this.querySelector('.collections-explorer__tabs');
      this.panelsEl = this.querySelector('.collections-explorer__panels');
      this.icons = {
        forward: this.getTemplateContent('chevron-forward'),
        back: this.getTemplateContent('chevron-back')
      };

      if (!this.tabsEl || !this.panelsEl) return;

      this.renderLoading();

      var self = this;
      var storefrontToken = this.storefrontToken;

      fetchExplorerData(this.apiUrl)
        .then(function (data) {
          return resolveCollectionHandles(collectCollectionIds(data), storefrontToken)
            .then(function (handleMap) {
              self.renderData(data, handleMap);
            });
        })
        .catch(function () { self.hidden = true; });
    }

    getTemplateContent(name) {
      var template = this.querySelector('template[data-template="' + name + '"]');
      return template ? template.content.firstElementChild : null;
    }

    renderLoading() {
      this.tabsEl.innerHTML = '';
      this.panelsEl.innerHTML = '';
      this.panelsEl.appendChild(el(
        'p',
        { class: 'collections-explorer__status' },
        this.locale === 'ar' ? 'جارٍ التحميل...' : 'Loading...'
      ));
    }

    renderData(data, handleMap) {
      var groupsRaw = Array.isArray(data && data.collections_explorer) ? data.collections_explorer : [];
      var brandsRaw = Array.isArray(data && data.brands) ? data.brands : [];
      var activeGroups = groupsRaw.filter(function (g) { return g && g.is_active; });
      var categoryGroups = activeGroups.filter(function (g) { return g.type !== 'boutique'; });
      var boutiqueGroup = activeGroups.filter(function (g) { return g.type === 'boutique'; })[0] || null;
      var boutiqueItems = boutiqueGroup
        ? (boutiqueGroup.group_collections || []).filter(function (c) { return c && c.is_active; })
        : [];
      var brands = brandsRaw.filter(function (b) { return b && b.is_active; });
      var locale = this.locale;
      var icons = this.icons;

      var tabDefs = [];

      if (categoryGroups.length) {
        tabDefs.push({
          key: 'categories',
          label: locale === 'ar' ? 'الفئات' : 'Categories',
          panel: buildCategoriesPanel(categoryGroups, locale, icons, handleMap)
        });
      }

      if (brands.length) {
        tabDefs.push({
          key: 'brands',
          label: locale === 'ar' ? 'البراندات' : 'Brands',
          panel: buildGridPanel(brands, locale, true, handleMap)
        });
      }

      if (boutiqueItems.length) {
        tabDefs.push({
          key: 'boutiques',
          label: locale === 'ar' ? 'بوتيكات' : 'Boutiques',
          panel: buildGridPanel(boutiqueItems, locale, false, handleMap)
        });
      }

      this.tabsEl.innerHTML = '';
      this.panelsEl.innerHTML = '';

      if (!tabDefs.length) {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      var self = this;
      tabDefs.forEach(function (tab, index) {
        var button = el('button', {
          type: 'button',
          class: 'collections-explorer__tab',
          role: 'tab',
          'aria-selected': index === 0 ? 'true' : 'false',
          'data-tab-key': tab.key
        }, tab.label);

        button.addEventListener('click', function () { self.activateTab(tab.key); });

        self.tabsEl.appendChild(button);

        tab.panel.setAttribute('data-tab-key', tab.key);
        tab.panel.hidden = index !== 0;
        self.panelsEl.appendChild(tab.panel);
      });
    }

    activateTab(key) {
      this.tabsEl.querySelectorAll('.collections-explorer__tab').forEach(function (button) {
        button.setAttribute('aria-selected', button.dataset.tabKey === key ? 'true' : 'false');
      });
      this.panelsEl.querySelectorAll('[data-tab-key]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-tab-key') !== key;
      });
    }
  };

  if (!window.customElements.get('collections-explorer-menu')) {
    window.customElements.define('collections-explorer-menu', CollectionsExplorerMenu);
  }
})();
